/**
 * @file Defines the App component which serves as the primary routing component in the application.
 * It configures the main application routes and ensures that the initial analytics setup is executed
 * on the first render.
 */
import React, { useEffect } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import AppPage from './pages/appPages/AppPage';
import { LandingPage } from './pages/LandingPage';
import PageLoader from './components/PageLoader';
import TermsOfUse from './pages/TermsOfUse';
import { UserContextWrapper } from './context';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserHydration } from './hooks/user';
import PrivacyPolicy from './pages/PrivacyPolicy';

/**
 * App component that sets up the main application routes.
 * On mount, it initialises analytics only once using the useEffect hook.
 *
 * It defines two routes:
 * - The root route ("/") which renders the LandingPage component.
 * - The "/app" route which renders the AppPage component.
 *
 * @returns {{}} A React Router `Routes` element containing the defined application routes.
 */
export const App = () => {
    return (
        <UserContextWrapper>
            <AppImpl />
        </UserContextWrapper>
    );
};

const AppImpl = () => {
    const { isAuthenticated, getAccessTokenSilently, isLoading } = useAuth0();

    useUserHydration();

    useEffect(() => {
        const fetchAccessToken = async () => {
            try {
                await getAccessTokenSilently(); // use this function to prevent full page redirect
            } catch (error) {
                console.error('Error fetching access token: ', error);
            }
        };
        if (isAuthenticated) {
            fetchAccessToken();
        }
    }, [isAuthenticated, getAccessTokenSilently]);

    // Even if tokens are cached in memory by default, getAccessTokenSilently would still send requests to the /token endpoint.
    // The PageLoader is displayed to indicate that processes, such as token retrieval, are in progress
    // and to prevent interaction with the app until the user's session is fully established.
    if (isLoading) {
        return <PageLoader />;
    }

    return (
        <Routes>
            <Route
                path='/'
                element={
                    isAuthenticated ? <Navigate to='/app' /> : <LandingPage />
                }
            />
            <Route path='/app' element={<AppPage />} />
            <Route path='/terms-of-use' element={<TermsOfUse />} />
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
        </Routes>
    );
};

export default App;
